'use client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FaPlus } from 'react-icons/fa';
import { useWatchlistContext } from '@benzinga/user-context';
import { sanitizeHTML } from '@benzinga/frontend-utils';
import type { StoryObject } from '@benzinga/basic-news-manager';
import type { SimpleNewsQuery } from '@benzinga/internal-news-manager';
import { fetchPopularNews, fetchWatchlistNews } from '../../news-actions';
import { getWatchlistNewsTicker, getWatchlistNewsTimestamp } from '../../watchlistNewsFetch';
import { BadgeProps } from './TradeIdeasBadge';
import { WatchlistNewsFeed } from './WatchlistNewsFeed';
import { EmptyStateProps, NewsListSkeleton, shortenTimeDateAgo } from './utils';

const defaultNewsQuery: SimpleNewsQuery = {};

type NewsItemData = StoryObject;

const newsCache: Record<string, NewsItemData[]> = {};

const NewsItem = ({
  article,
  showTimeAgo = false,
  compact = false,
}: {
  article: NewsItemData;
  showTimeAgo?: boolean;
  compact?: boolean;
}) => {
  const { title, url } = article;
  const ticker = getWatchlistNewsTicker(article);
  const updated = getWatchlistNewsTimestamp(article);

  return (
    <div
      className={
        compact
          ? 'group flex items-start gap-3 rounded-lg px-2.5 py-2.5 transition-colors duration-150 ease-out hover:bg-slate-50'
          : 'flex items-start gap-3 border-b border-slate-100 py-3.5 first:pt-0 last:border-b-0 last:pb-0'
      }
    >
      {ticker ? (
        <a
          aria-label={`View quote for ${ticker}`}
          className="shrink-0 pt-0.5"
          href={`https://www.benzinga.com/quote/${ticker}`}
        >
          <span className="inline-flex min-w-[2.75rem] justify-center rounded-md bg-slate-100 px-2 py-0.5 text-xs font-bold tabular-nums text-bzblue-800 transition-colors duration-150 group-hover:bg-bzblue-700 group-hover:text-white hover:bg-bzblue-700 hover:text-white">
            {ticker}
          </span>
        </a>
      ) : (
        <span className="inline-flex min-w-[2.75rem] shrink-0 justify-center rounded-md bg-transparent px-2 py-0.5 text-xs opacity-0">
          —
        </span>
      )}
      <a aria-label="Read article" href={url} className="min-w-0 flex-1 no-underline">
        <p
          dangerouslySetInnerHTML={{ __html: sanitizeHTML(title ?? '') }}
          className={`text-pretty font-semibold leading-snug text-slate-900 transition-colors duration-150 group-hover:text-bzblue-800 ${
            compact ? 'text-sm' : 'text-sm sm:text-[15px]'
          }`}
        />
        {showTimeAgo ? (
          <span className="mt-1 block text-[11px] font-medium tabular-nums text-slate-400">
            {shortenTimeDateAgo(updated)}
          </span>
        ) : null}
      </a>
    </div>
  );
};

export const ArticleNewsFeed = ({
  newsQuery = defaultNewsQuery,
  badge,
  title,
  showTimeAgo = false,
  isPopularFeed = false,
  embedded = false,
  compact = false,
  hideTitle = false,
  skeletonCount,
}: {
  newsQuery?: SimpleNewsQuery;
  badge?: BadgeProps;
  title: string;
  showTimeAgo?: boolean;
  isPopularFeed?: boolean;
  embedded?: boolean;
  compact?: boolean;
  hideTitle?: boolean;
  skeletonCount?: number;
}) => {
  const { selectedWatchlist, setAddTickerModal } = useWatchlistContext();

  const newsQueryKey = useMemo(() => {
    if (!newsQuery) return '';
    return JSON.stringify(newsQuery, Object.keys(newsQuery).sort());
  }, [newsQuery]);

  const cacheKey = useMemo(() => {
    if (isPopularFeed) return `popular:${newsQueryKey}`;
    return `${selectedWatchlist?.watchlistId ?? 'none'}:${newsQueryKey}`;
  }, [isPopularFeed, newsQueryKey, selectedWatchlist?.watchlistId]);

  const cachedNews = newsCache[cacheKey];
  const [news, setNews] = useState<NewsItemData[]>(cachedNews ?? []);
  const [isLoading, setIsLoading] = useState(!cachedNews);
  const [hasFetchError, setHasFetchError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [activeCacheKey, setActiveCacheKey] = useState(cacheKey);

  if (cacheKey !== activeCacheKey) {
    const nextCached = newsCache[cacheKey];
    setActiveCacheKey(cacheKey);
    setNews(nextCached ?? []);
    setIsLoading(!nextCached);
    setHasFetchError(false);
  }

  const handleRetry = useCallback(() => {
    delete newsCache[cacheKey];
    setHasFetchError(false);
    setIsLoading(true);
    setRetryCount(count => count + 1);
  }, [cacheKey]);

  useEffect(() => {
    const abortController = new AbortController();
    const watchlistSymbols = selectedWatchlist?.symbols.map(symbol => symbol.symbol) ?? [];

    const fetchNews = async () => {
      const existing = newsCache[cacheKey];
      if (existing) {
        setNews(existing);
        setHasFetchError(false);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setHasFetchError(false);

      try {
        if (isPopularFeed) {
          const result = await fetchPopularNews();
          if (abortController.signal.aborted) return;

          if (result.status === 'error') {
            setHasFetchError(true);
            setNews([]);
            return;
          }

          newsCache[cacheKey] = result.news;
          setNews(result.news);
          return;
        }

        if (watchlistSymbols.length === 0) {
          setNews([]);
          return;
        }

        const result = await fetchWatchlistNews(watchlistSymbols, newsQuery);
        if (abortController.signal.aborted) return;

        if (result.status === 'error') {
          setHasFetchError(true);
          setNews([]);
          return;
        }

        newsCache[cacheKey] = result.news;
        setNews(result.news);
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') return;
        console.error('Error fetching news:', error);
        setHasFetchError(true);
        setNews([]);
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    if (isPopularFeed || watchlistSymbols.length > 0) {
      fetchNews();
    } else {
      setNews([]);
      setHasFetchError(false);
      setIsLoading(false);
    }

    return () => {
      abortController.abort();
    };
  }, [
    cacheKey,
    isPopularFeed,
    newsQuery,
    retryCount,
    selectedWatchlist?.symbols,
    selectedWatchlist?.watchlistId,
  ]);

  const resolvedSkeletonCount = skeletonCount ?? (embedded ? 5 : 6);
  const showSkeleton = isLoading && news.length === 0 && !hasFetchError;
  const watchlistSymbolCount = selectedWatchlist?.symbols.length ?? 0;
  const hasWatchlistSymbols = watchlistSymbolCount > 0;
  const isCategoryFiltered = newsQuery?.channels !== undefined;

  const emptyState = useMemo((): EmptyStateProps | undefined => {
    if (hasFetchError) {
      return {
        title: "Couldn't load headlines",
        description: 'Something went wrong while fetching news. Please try again.',
        action: {
          label: 'Retry',
          onClick: handleRetry,
        },
      };
    }

    if (isPopularFeed) {
      return {
        title: 'No popular headlines right now',
        description: 'Check back soon for the latest market-moving stories.',
      };
    }

    if (!hasWatchlistSymbols) {
      return {
        title: 'Add tickers to see news',
        description: 'Headlines for your watchlist symbols will appear here once you add a few tickers.',
        icon: FaPlus,
        action: {
          label: 'Add tickers',
          onClick: () => setAddTickerModal({ show: true, ticker: null, isLoading: false }),
        },
      };
    }

    if (isCategoryFiltered) {
      return {
        title: `No ${title.toLowerCase()} headlines`,
        description: 'Nothing in this category right now. Try another filter or check back soon.',
      };
    }

    return {
      title: 'No headlines right now',
      description: "We're not seeing news for your watchlist symbols at the moment. Check back soon.",
    };
  }, [handleRetry, hasFetchError, hasWatchlistSymbols, isCategoryFiltered, isPopularFeed, setAddTickerModal, title]);

  return (
    <WatchlistNewsFeed
      title={title}
      badge={badge}
      isLoading={showSkeleton}
      newsCount={news.length}
      embedded={embedded}
      hideTitle={hideTitle}
      emptyState={emptyState}
      skeleton={
        <NewsListSkeleton
          compact={compact || embedded}
          count={resolvedSkeletonCount}
          showTimeAgo={showTimeAgo}
        />
      }
    >
      {news.map(article => (
        <NewsItem key={article.id} article={article} showTimeAgo={showTimeAgo} compact={compact} />
      ))}
    </WatchlistNewsFeed>
  );
};
